import React, { useState, useCallback } from 'react';
import ImageUpload from '../ImageUpload';
import {
    addPhotoshoot,
    updatePhotoshoot,
    deletePhotoshoot,
    addPhotoToShoot,
    loadPhotoshootPhotos
} from '../../../services/dataService';
import { updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { deleteImage } from '../../../services/dataService';

const PhotographyAdmin = ({ siteData, updateSiteData, setSiteData, refreshCollectionData, showSaveStatus }) => {
    const [saving, setSaving] = useState(false);
    const [pendingUpdates, setPendingUpdates] = useState({});
    const [expandedPhotoshoot, setExpandedPhotoshoot] = useState(null);
    const [photoshootPhotos, setPhotoshootPhotos] = useState({});
    const [loadingPhotos, setLoadingPhotos] = useState({});

    // Debounced save function
    const debouncedSave = useCallback(
        debounce(async (photoshootId, updates) => {
            try {
                await updatePhotoshoot(photoshootId, updates);
                showSaveStatus('Changes saved automatically');

                // Clear this photoshoot from pending updates
                setPendingUpdates(prev => {
                    const newPending = { ...prev };
                    delete newPending[photoshootId];
                    return newPending;
                });
            } catch (error) {
                console.error('Failed to auto-save:', error);
                showSaveStatus('Auto-save failed', true);
            }
        }, 1000), // Save 1 second after user stops typing
        []
    );

    // Load photos for a specific photoshoot
    const loadPhotosForShoot = async (photoshootId) => {
        try {
            setLoadingPhotos(prev => ({ ...prev, [photoshootId]: true }));
            const photos = await loadPhotoshootPhotos(photoshootId);
            setPhotoshootPhotos(prev => ({
                ...prev,
                [photoshootId]: photos
            }));

            // Update photo count in parent photoshoot
            await updatePhotoshootPhotoCount(photoshootId, photos.length);
        } catch (error) {
            console.error('Failed to load photoshoot photos:', error);
            showSaveStatus('Failed to load photos', true);
        } finally {
            setLoadingPhotos(prev => ({ ...prev, [photoshootId]: false }));
        }
    };

    // Toggle photoshoot expansion and load photos
    const togglePhotoshootExpansion = async (photoshootId) => {
        if (expandedPhotoshoot === photoshootId) {
            setExpandedPhotoshoot(null);
        } else {
            setExpandedPhotoshoot(photoshootId);
            if (!photoshootPhotos[photoshootId]) {
                await loadPhotosForShoot(photoshootId);
            }
        }
    };

    // Add new photoshoot
    const handleAddPhotoshoot = async () => {
        try {
            setSaving(true);
            const newPhotoshoot = {
                title: 'New Photoshoot',
                description: '',
                coverImage: '',
                coverImagePath: '',
                date: new Date().toISOString().split('T')[0],
                location: '',
                photoCount: 0
            };

            await addPhotoshoot(newPhotoshoot);
            await refreshCollectionData('photoshoots');
            showSaveStatus('Photoshoot added successfully!');
        } catch (error) {
            console.error('Failed to add photoshoot:', error);
            showSaveStatus('Failed to add photoshoot', true);
        } finally {
            setSaving(false);
        }
    };

    // Delete photoshoot
    const handleDeletePhotoshoot = async (photoshoot) => {
        if (window.confirm('Are you sure you want to delete this photoshoot? This will also delete all photos in it.')) {
            try {
                setSaving(true);
                await deletePhotoshoot(photoshoot.id, photoshoot.photos || []);
                await refreshCollectionData('photoshoots');
                showSaveStatus('Photoshoot deleted successfully!');
            } catch (error) {
                console.error('Failed to delete photoshoot:', error);
                showSaveStatus('Failed to delete photoshoot', true);
            } finally {
                setSaving(false);
            }
        }
    };

    // Update photoshoot fields with debouncing
    const handlePhotoshootUpdate = (photoshootId, field, value) => {
        // Update local state immediately
        const updatedPhotoshoots = siteData.photography.photoshoots.map(p =>
            p.id === photoshootId ? { ...p, [field]: value } : p
        );

        setSiteData(prev => ({
            ...prev,
            photography: {
                ...prev.photography,
                photoshoots: updatedPhotoshoots
            }
        }));

        // Track pending updates
        setPendingUpdates(prev => ({
            ...prev,
            [photoshootId]: {
                ...prev[photoshootId],
                [field]: value
            }
        }));

        // Get the updated photoshoot data
        const updatedPhotoshoot = updatedPhotoshoots.find(p => p.id === photoshootId);

        // Debounced save
        debouncedSave(photoshootId, updatedPhotoshoot);
    };

    // Handle image upload for photoshoot cover
    const handleImageUpload = async (photoshootId, field, pathField, imageUrl, imagePath) => {
        try {
            const photoshootToUpdate = siteData.photography.photoshoots?.find(p => p.id === photoshootId);
            const updatedPhotoshoot = {
                ...photoshootToUpdate,
                [field]: imageUrl,
                [pathField]: imagePath
            };

            // Update database immediately for images
            await updatePhotoshoot(photoshootId, updatedPhotoshoot);

            // Update local state
            const updatedPhotoshoots = siteData.photography.photoshoots.map(p =>
                p.id === photoshootId ? updatedPhotoshoot : p
            );

            setSiteData(prev => ({
                ...prev,
                photography: {
                    ...prev.photography,
                    photoshoots: updatedPhotoshoots
                }
            }));

            showSaveStatus('Cover image uploaded successfully!');
        } catch (error) {
            console.error('Failed to update cover image:', error);
            showSaveStatus('Failed to update cover image', true);
        }
    };

    // Add photo to photoshoot
    const handleAddPhotoToShoot = async (photoshootId) => {
        console.log('Adding photo to photoshoot:', photoshootId);

        try {
            const newPhoto = {
                title: 'New Photo',
                description: '',
                url: '',
                imagePath: '',
                createdAt: new Date(),
                updatedAt: new Date()
            };

            console.log('Creating new photo document...');
            const docRef = await addPhotoToShoot(photoshootId, newPhoto);
            console.log('Photo document created with ID:', docRef);

            // Add the photo with its ID to local state immediately
            const photoWithId = { ...newPhoto, id: docRef };
            const updatedPhotos = [...(photoshootPhotos[photoshootId] || []), photoWithId];

            setPhotoshootPhotos(prev => ({
                ...prev,
                [photoshootId]: updatedPhotos
            }));

            // Update the parent photoshoot's photo count
            await updatePhotoshootPhotoCount(photoshootId, updatedPhotos.length);

            showSaveStatus('Photo added to photoshoot!');
        } catch (error) {
            console.error('Failed to add photo:', error);
            showSaveStatus('Failed to add photo: ' + error.message, true);
        }
    };

    // Update photo in photoshoot
    const handlePhotoUpdate = async (photoshootId, photoId, field, value) => {
        try {
            const photos = photoshootPhotos[photoshootId] || [];
            const photoToUpdate = photos.find(p => p.id === photoId);
            const updatedPhoto = { ...photoToUpdate, [field]: value };

            // Update in Firestore
            await updateDoc(doc(db, 'photoshoots', photoshootId, 'photos', photoId), {
                ...updatedPhoto,
                updatedAt: new Date()
            });

            // Update local state
            const updatedPhotos = photos.map(p => p.id === photoId ? updatedPhoto : p);
            setPhotoshootPhotos(prev => ({
                ...prev,
                [photoshootId]: updatedPhotos
            }));

            showSaveStatus('Photo updated!');
        } catch (error) {
            console.error('Failed to update photo:', error);
            showSaveStatus('Failed to update photo', true);
        }
    };

    // Delete photo from photoshoot
    const handleDeletePhoto = async (photoshootId, photoId, imagePath) => {
        if (window.confirm('Are you sure you want to delete this photo?')) {
            try {
                await deleteDoc(doc(db, 'photoshoots', photoshootId, 'photos', photoId));

                if (imagePath) {
                    await deleteImage(imagePath);
                }

                await loadPhotosForShoot(photoshootId);

                // Update the parent photoshoot's photo count
                const updatedPhotos = photoshootPhotos[photoshootId]?.filter(p => p.id !== photoId) || [];
                await updatePhotoshootPhotoCount(photoshootId, updatedPhotos.length);

                showSaveStatus('Photo deleted!');
            } catch (error) {
                console.error('Failed to delete photo:', error);
                showSaveStatus('Failed to delete photo', true);
            }
        }
    };

    // Update photoshoot photo count
    const updatePhotoshootPhotoCount = async (photoshootId, photoCount) => {
        try {
            // Update in Firestore
            await updatePhotoshoot(photoshootId, { photoCount });

            // Update local state
            const updatedPhotoshoots = siteData.photography.photoshoots.map(p =>
                p.id === photoshootId ? { ...p, photoCount } : p
            );

            setSiteData(prev => ({
                ...prev,
                photography: {
                    ...prev.photography,
                    photoshoots: updatedPhotoshoots
                }
            }));
        } catch (error) {
            console.error('Failed to update photo count:', error);
        }
    };

    // Handle photo image upload
    // Handle photo image upload
    const handlePhotoImageUpload = async (photoshootId, photoId, imageUrl, imagePath) => {
        console.log('Uploading image for photo:', { photoshootId, photoId, imageUrl, imagePath });

        try {
            // Update both URL and path in a single operation
            const photos = photoshootPhotos[photoshootId] || [];
            const photoToUpdate = photos.find(p => p.id === photoId);

            if (!photoToUpdate) {
                console.error('Photo not found:', photoId);
                showSaveStatus('Photo not found', true);
                return;
            }

            const updatedPhoto = {
                ...photoToUpdate,
                url: imageUrl,
                imagePath: imagePath,
                updatedAt: new Date()
            };

            console.log('Updating photo in Firestore:', updatedPhoto);

            // Update in Firestore
            await updateDoc(doc(db, 'photoshoots', photoshootId, 'photos', photoId), updatedPhoto);

            // Update local state
            const updatedPhotos = photos.map(p => p.id === photoId ? updatedPhoto : p);
            setPhotoshootPhotos(prev => ({
                ...prev,
                [photoshootId]: updatedPhotos
            }));

            console.log('Photo image uploaded successfully');
            showSaveStatus('Photo image uploaded successfully!');
        } catch (error) {
            console.error('Failed to upload photo image:', error);
            showSaveStatus('Failed to upload photo image: ' + error.message, true);
        }
    };

    // Update photography section title
    const updatePhotographyData = async (updates) => {
        try {
            const updatedPhotography = {
                ...siteData.photography,
                ...updates
            };

            setSiteData(prev => ({
                ...prev,
                photography: updatedPhotography
            }));

            await updateSiteData('photography', updatedPhotography);
        } catch (error) {
            console.error('Failed to update photography:', error);
            showSaveStatus('Failed to update photography section', true);
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Photography Photoshoots</h2>
                <button
                    onClick={handleAddPhotoshoot}
                    disabled={saving}
                    className="bg-sage-600 text-white px-4 py-2 rounded-lg hover:bg-sage-700 transition-colors disabled:opacity-50"
                >
                    {saving ? 'Adding...' : 'Add Photoshoot'}
                </button>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                <input
                    type="text"
                    value={siteData.photography.title || ''}
                    onChange={(e) => updatePhotographyData({ title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sage-500 focus:border-transparent"
                    placeholder="Photography Portfolio"
                />
            </div>

            <div className="space-y-6">
                {siteData.photography.photoshoots?.map((photoshoot) => (
                    <div key={photoshoot.id} className="border border-gray-200 rounded-lg p-6 relative">
                        {/* Pending save indicator */}
                        {pendingUpdates[photoshoot.id] && (
                            <div className="absolute top-2 right-16 text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                                Saving...
                            </div>
                        )}

                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-lg font-medium text-gray-900">Photoshoot: {photoshoot.title}</h3>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => togglePhotoshootExpansion(photoshoot.id)}
                                    className="text-sage-600 hovertext-sage-700 text-sm font-medium"
                                >
                                    {expandedPhotoshoot === photoshoot.id ? 'Hide Photos' : 'Manage Photos'}
                                </button>
                                <button
                                    onClick={() => handleDeletePhotoshoot(photoshoot)}
                                    disabled={saving}
                                    className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                                    <input
                                        type="text"
                                        value={photoshoot.title}
                                        onChange={(e) => handlePhotoshootUpdate(photoshoot.id, 'title', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                                    <textarea
                                        value={photoshoot.description}
                                        onChange={(e) => handlePhotoshootUpdate(photoshoot.id, 'description', e.target.value)}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                                        <input
                                            type="date"
                                            value={photoshoot.date}
                                            onChange={(e) => handlePhotoshootUpdate(photoshoot.id, 'date', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                                        <input
                                            type="text"
                                            value={photoshoot.location || ''}
                                            onChange={(e) => handlePhotoshootUpdate(photoshoot.id, 'location', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-1 focus:ring-sage-500"
                                            placeholder="Location"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Cover Image</label>
                                <ImageUpload
                                    currentImage={photoshoot.coverImage}
                                    currentImagePath={photoshoot.coverImagePath}
                                    onImageUpload={(imageUrl, imagePath) =>
                                        handleImageUpload(photoshoot.id, 'coverImage', 'coverImagePath', imageUrl, imagePath)
                                    }
                                    label="Upload Cover Image"
                                    folder={`photography/${photoshoot.id}`}
                                    aspectRatio={1}
                                />
                            </div>
                        </div>

                        {/* Expanded Photos Section */}
                        {expandedPhotoshoot === photoshoot.id && (
                            <div className="mt-8 border-t pt-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-lg font-medium text-gray-900">Photos in this shoot</h4>
                                    <button
                                        onClick={() => handleAddPhotoToShoot(photoshoot.id)}
                                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                                    >
                                        Add Photo
                                    </button>
                                </div>

                                {loadingPhotos[photoshoot.id] ? (
                                    <div className="flex justify-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-600"></div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {photoshootPhotos[photoshoot.id]?.map((photo) => (
                                            <div key={photo.id} className="border rounded-lg p-4 bg-gray-50">
                                                <div className="aspect-square mb-3">
                                                    <ImageUpload
                                                        currentImage={photo.url}
                                                        currentImagePath={photo.imagePath}
                                                        onImageUpload={(imageUrl, imagePath) =>
                                                            handlePhotoImageUpload(photoshoot.id, photo.id, imageUrl, imagePath)
                                                        }
                                                        label="Upload Photo"
                                                        folder={`photography/${photoshoot.id}/photos`}
                                                        aspectRatio={1}
                                                    />
                                                </div>

                                                <div className="space-y-2">
                                                    <input
                                                        type="text"
                                                        value={photo.title || ''}
                                                        onChange={(e) => handlePhotoUpdate(photoshoot.id, photo.id, 'title', e.target.value)}
                                                        placeholder="Photo title"
                                                        className="w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-sage-500"
                                                    />

                                                    <textarea
                                                        value={photo.description || ''}
                                                        onChange={(e) => handlePhotoUpdate(photoshoot.id, photo.id, 'description', e.target.value)}
                                                        placeholder="Photo description"
                                                        rows={2}
                                                        className="w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-sage-500"
                                                    />

                                                    <button
                                                        onClick={() => handleDeletePhoto(photoshoot.id, photo.id, photo.imagePath)}
                                                        className="w-full text-red-600 hover:text-red-800 text-xs py-1 hover:bg-red-50 rounded transition-colors"
                                                    >
                                                        Delete Photo
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {(!photoshootPhotos[photoshoot.id] || photoshootPhotos[photoshoot.id].length === 0) && !loadingPhotos[photoshoot.id] && (
                                    <div className="text-center py-8 text-gray-500">
                                        <p>No photos in this photoshoot yet. Click "Add Photo" to get started.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}

                {(!siteData.photography.photoshoots || siteData.photography.photoshoots.length === 0) && (
                    <div className="text-center py-12 text-gray-500">
                        <p>No photoshoots added yet. Click "Add Photoshoot" to get started.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

// Debounce utility function
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

export default PhotographyAdmin;